import * as z from 'zod';
import { organizationType } from '../organizations';
import { campaignStatusEnum } from '../campaigns';
import { readLandingPageSchema } from '../landing-pages';
import { companySchema } from '../companies';
import { embeddedBlockVariantSchema } from '../block-variants';
import { pageStatus } from '../pages';

// Models for GET /api/v1/public/outreach-demos/{id} — the single fully
// public (unauthenticated) call powering the outreach demo previewer. `{id}`
// is a demo id (the share token) or, for links minted before the demo
// entity, an organization id. The response shape is fixed server-side (no
// `fields` projection) and the endpoint 404s for anything that is not a demo.
// The back-office side (settings, share preview) lives further down.

// Layer 0 — enums
export const outreachDemoLanguageEnum = z.enum(['en', 'fr']);
export type OutreachDemoLanguage = z.infer<typeof outreachDemoLanguageEnum>;

// Publication of the demo's static site (index.html with the embedded payload
// + the previewer assets on the campaigns CDN). Draft/failed demos are still
// served live through the previewer + public API.
export const outreachDemoStatusEnum = z.enum([
  'draft',
  'publishing',
  'published',
  'failed',
]);
export type OutreachDemoStatus = z.infer<typeof outreachDemoStatusEnum>;

// Organization subset exposed to the anonymous caller.
export const readOutreachDemoOrganizationSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  type: organizationType.nullable(),
  domain: z.string().nullable(),
});
export type ReadOutreachDemoOrganization = z.infer<
  typeof readOutreachDemoOrganizationSchema
>;

// Sidebar entry: one lead per target account, with the company's logo pool.
export const readOutreachDemoLeadSchema = z.object({
  id: z.uuid().nullable(),
  company: z.lazy(() => companySchema).nullable(),
});
export type ReadOutreachDemoLead = z.infer<typeof readOutreachDemoLeadSchema>;

// One page per lead (join on `lead.id`), carrying its block variants inline.
export const readOutreachDemoPageSchema = z.object({
  id: z.uuid().nullable(),
  status: pageStatus.nullable(),
  errorCode: z.string().nullable(),
  lead: z.object({ id: z.uuid().nullable() }).nullable(),
  blockVariants: z.array(z.lazy(() => embeddedBlockVariantSchema)).nullable(),
});
export type ReadOutreachDemoPage = z.infer<typeof readOutreachDemoPageSchema>;

export const readOutreachDemoCampaignSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  status: campaignStatusEnum.nullable(),
  landingPage: z.lazy(() => readLandingPageSchema).nullable(),
  leads: z.array(readOutreachDemoLeadSchema).nullable(),
  pages: z.array(readOutreachDemoPageSchema).nullable(),
});
export type ReadOutreachDemoCampaign = z.infer<
  typeof readOutreachDemoCampaignSchema
>;

// The previewer's call to action, effective value (override or default).
// `url` null => no button.
export const readOutreachDemoCtaSchema = z.object({
  label: z.string().nullable(),
  url: z.string().nullable(),
});
export type ReadOutreachDemoCta = z.infer<typeof readOutreachDemoCtaSchema>;

// Public slice of the demo's settings: what the previewer renders. `id` is
// the share token (null only for demos older than the demo entity).
export const readOutreachDemoSettingsSchema = z.object({
  id: z.uuid().nullable(),
  language: outreachDemoLanguageEnum.nullable(),
  cta: readOutreachDemoCtaSchema.nullable(),
});
export type ReadOutreachDemoSettings = z.infer<
  typeof readOutreachDemoSettingsSchema
>;

// The payload grows as the funnel runs: the whole call 404s until the
// campaign exists, `leads` fills after lead import, `pages` after audience
// validation — every collection stays nullable.
export const readOutreachDemoSchema = z.object({
  organization: readOutreachDemoOrganizationSchema.nullable(),
  campaign: readOutreachDemoCampaignSchema.nullable(),
  demo: readOutreachDemoSettingsSchema.nullable(),
});
export type ReadOutreachDemo = z.infer<typeof readOutreachDemoSchema>;

// ── The demo row (back office) ──────────────────────────────────────────────
// GET/PATCH /api-tools/v1/outreach/demos/{demoId}, POST …/share-preview.

// The raw back-office overrides; null = the default applies.
export const readOutreachDemoOverridesSchema = z.object({
  language: outreachDemoLanguageEnum.nullable(),
  ctaLabel: z.string().nullable(),
  ctaUrl: z.string().nullable(),
  ogTitle: z.string().nullable(),
  ogDescription: z.string().nullable(),
});
export type ReadOutreachDemoOverrides = z.infer<
  typeof readOutreachDemoOverridesSchema
>;

// What the demo actually shows: every override applied over its default.
export const readOutreachDemoEffectiveSchema = z.object({
  language: outreachDemoLanguageEnum.nullable(),
  ctaLabel: z.string().nullable(),
  ctaUrl: z.string().nullable(),
  ogTitle: z.string().nullable(),
  ogDescription: z.string().nullable(),
});
export type ReadOutreachDemoEffective = z.infer<
  typeof readOutreachDemoEffectiveSchema
>;

// The publication of the demo as a static site. Always present on the admin
// row: a draft carries the status and the share link (publicUrl) but no
// pageUrl/imageUrl yet. `stale`: published before the demo or its campaign
// last changed — republish to refresh.
export const readOutreachDemoPublicationSchema = z.object({
  demoId: z.uuid().nullable(),
  status: outreachDemoStatusEnum.nullable(),
  publicUrl: z.string().nullable(),
  pageUrl: z.string().nullable(),
  imageUrl: z.string().nullable(),
  language: outreachDemoLanguageEnum.nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  publishedAt: z.iso.datetime().nullable(),
  publishError: z.string().nullable(),
  stale: z.boolean().nullable(),
});
export type ReadOutreachDemoPublication = z.infer<
  typeof readOutreachDemoPublicationSchema
>;

export const readOutreachDemoAdminSchema = z.object({
  id: z.uuid().nullable(),
  organization: z.object({ id: z.uuid().nullable() }).nullable(),
  // Null until the funnel's create_campaign step.
  campaign: z.object({ id: z.uuid().nullable() }).nullable(),
  job: z.object({ id: z.uuid().nullable() }).nullable(),
  domain: z.string().nullable(),
  landingPageUrl: z.string().nullable(),
  // Detected from the prospect's brand by the funnel (system-managed).
  detectedLanguage: outreachDemoLanguageEnum.nullable(),
  overrides: readOutreachDemoOverridesSchema.nullable(),
  effective: readOutreachDemoEffectiveSchema.nullable(),
  publication: readOutreachDemoPublicationSchema.nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadOutreachDemoAdmin = z.infer<typeof readOutreachDemoAdminSchema>;

// PATCH payload — every field optional: omitted = unchanged, '' = clear the
// override (back to the default), value = the new override. `ogTitle` /
// `ogDescription` may use {domain} and {n}.
const outreachDemoOverride = (max: number) =>
  z.string().max(max, `Must be at most ${max} characters`).optional();
export const updateOutreachDemoSettingsSchema = z.object({
  language: z.union([z.literal(''), outreachDemoLanguageEnum]).optional(),
  ctaLabel: outreachDemoOverride(120),
  ctaUrl: z.union([z.literal(''), z.url('Must be a valid URL')]).optional(),
  ogTitle: outreachDemoOverride(200),
  ogDescription: outreachDemoOverride(500),
});
export type UpdateOutreachDemoSettings = z.infer<
  typeof updateOutreachDemoSettingsSchema
>;

// ── The funnel that creates a demo (back office) ────────────────────────────

/** Job name used by the outreach demo funnel orchestration job. */
export const OUTREACH_DEMO_FUNNEL_JOB_NAME = 'outreach_demo_funnel';

export const outreachDemoFunnelStepEnum = z.enum([
  'adjust_quotas',
  'brand_extraction',
  'generate_icps',
  'import_buyers',
  'generate_audience',
  'import_leads',
  'create_campaign',
  'block_identification',
  'enable_all_blocks',
  'attach_audience',
  'validate_audience',
  'generate_pages',
  'confirm_steps',
  'done',
]);
export type OutreachDemoFunnelStep = z.infer<typeof outreachDemoFunnelStepEnum>;

// Codes follow `<step>_failed` / `<step>_timeout` plus the named ones below —
// the list is not exhaustive, presentation must tolerate unknown codes.
export const outreachDemoFunnelErrorCodeEnum = z.enum([
  'brand_extraction_failed',
  'brand_extraction_timeout',
  'icp_generation_failed',
  'buyer_pool_too_small',
  'audience_generation_failed',
  'lead_import_failed',
  'no_blocks_identified',
  'audience_conflict',
  'no_generation_started',
  'page_generation_failed',
  'page_generation_timeout',
  'funnel_rejected',
  'interrupted',
  'outreach_demo_error',
]);
export type OutreachDemoFunnelErrorCode = z.infer<
  typeof outreachDemoFunnelErrorCodeEnum
>;

// Shape of the funnel job's `state` payload (`ReadJob.state` is untyped —
// parse it with `parseOutreachDemoFunnelState`). Result ids only appear once
// the run has reached the matching step.
export const outreachDemoFunnelStateSchema = z.object({
  step: outreachDemoFunnelStepEnum.nullish().catch(null),
  // The prospect's domain, present from job creation on (every state write carries it).
  domain: z.string().nullish().catch(null),
  // The demo row's id — the share token and the key of the back-office
  // settings. Absent on runs older than the demo entity.
  demoId: z.uuid().nullish().catch(null),
  // The page the campaign is built on: the URL as entered when the run was
  // started, or the homepage of `domain`. Absent on runs older than it (homepage).
  landingPageUrl: z.string().nullish().catch(null),
  // True when the run reused the domain's existing demo organization (and
  // inherited its brand, buyer pool and audience) instead of creating one.
  organizationReused: z.boolean().nullish().catch(null),
  // The share link, set at completion when the demo's static site was published.
  publicUrl: z.string().nullish().catch(null),
  organizationId: z.uuid().nullish().catch(null),
  campaignId: z.uuid().nullish().catch(null),
  audienceId: z.uuid().nullish().catch(null),
  buyersImported: z.number().nullish().catch(null),
  leadsImported: z.number().nullish().catch(null),
  campaignStatus: z.string().nullish().catch(null),
  // Set once the run has been resumed at least once (POST .../{jobId}/resume).
  resumedCount: z.number().nullish().catch(null),
  resumedAt: z.string().nullish().catch(null),
});
export type OutreachDemoFunnelState = z.infer<
  typeof outreachDemoFunnelStateSchema
>;

/** Safely read a job's `state` as an outreach demo funnel state. */
export const parseOutreachDemoFunnelState = (
  state: unknown,
): OutreachDemoFunnelState | null => {
  const result = outreachDemoFunnelStateSchema.safeParse(state);
  return result.success ? result.data : null;
};

// Create schema — payload of POST /api-tools/v1/outreach/demos
export const startOutreachDemoSchema = z.object({
  // A bare domain ("acme.com") builds the demo on the homepage; a full URL
  // ("https://acme.com/pricing") builds it on that page. The demo organization
  // takes the domain either way.
  domain: z.string().min(1, 'Domain or page URL is required'),
  audienceSize: z
    .number()
    .int('Audience size must be a whole number')
    .min(1, 'Audience size must be at least 1')
    .max(100, 'Audience size must be at most 100')
    .optional(),
});
export type StartOutreachDemo = z.infer<typeof startOutreachDemoSchema>;

// Read schema — immediate response of the start and resume calls (the funnel
// runs in the background; follow it through the returned job). On resume the
// job id is the one passed in: the run restarts in place, nothing is duplicated.
export const readOutreachDemoRunSchema = z.object({
  organization: z.object({ id: z.uuid().nullable() }).nullable(),
  job: z.object({ id: z.uuid().nullable() }).nullable(),
  demo: z.object({ id: z.uuid().nullable() }).nullable(),
});
export type ReadOutreachDemoRun = z.infer<typeof readOutreachDemoRunSchema>;
